/**
 * Rebuilds feed from DB history + loads pinned artifacts for a session.
 */
import { useDevAgentStore, type FeedItem } from "@/stores/devAgentStore";
import { getBackendArtifact, getDevAgentSessionHistory, listArtifacts, listBackendArtifacts } from "../devAgentService";
import { normalizeAssistantContent } from "../normalizeAssistantContent";

function shouldSkipAssistantHistoryMessage(content: string, meta: Record<string, any>) {
  const stripped = (content || "").trim();
  if (!stripped) return true;
  if (stripped === "*Processing your request…*" || stripped === "*Processing your request...*") {
    return true;
  }
  if ((meta?.baseline || meta?.streaming_baseline) && !meta?.run_status) {
    return true;
  }
  const normalized = normalizeAssistantContent(stripped);
  return !normalized.trim();
}

export async function hydrateDevAgentFeedFromHistory(
  sessionUuid: string,
  signal: AbortSignal,
): Promise<void> {
  const history = await getDevAgentSessionHistory(sessionUuid);
  if (signal.aborted) return;
  // Avoid applying session A's history after the user switched to session B.
  if (useDevAgentStore.getState().sessionUuid !== sessionUuid) return;
  if (!Array.isArray(history) || history.length === 0) return;

  const items: FeedItem[] = [];
  for (const msg of history) {
    const meta = msg.metadata_ || msg.metadata || {};
    const ts = msg.created_at ? new Date(msg.created_at).getTime() : Date.now();

    if (msg.role === "user") {
      items.push({
        id: crypto.randomUUID(),
        type: "chat_user",
        timestamp: ts,
        data: { content: msg.content },
      });
      continue;
    }

    const toolCalls: any[] = meta.tool_calls || [];
    for (const tc of toolCalls) {
      items.push({
        id: crypto.randomUUID(),
        type: "tool_call",
        timestamp: ts,
        data: {
          name: tc.name || tc.tool || "tool",
          args: tc.arguments || tc.args,
          status: tc.error ? "error" : "done",
          output: tc.result || tc.output,
          error: tc.error,
          durationMs: tc.duration_ms,
        },
      });
    }

    const plan = meta.plan;
    if (plan) {
      const steps = (plan.steps || plan.subtasks || []).map((s: any, i: number) => ({
        step_number: s.step_number || i + 1,
        description: s.description || s.label || `Step ${i + 1}`,
        status: s.status || "completed",
      }));
      if (steps.length > 0) {
        items.push({
          id: crypto.randomUUID(),
          type: "plan",
          timestamp: ts,
          data: { steps, summary: plan.summary || "Plan", total: steps.length },
        });
      }
    }

    const codeChanges: any[] = meta.code_changes || [];
    for (const cc of codeChanges) {
      items.push({
        id: crypto.randomUUID(),
        type: "code_change",
        timestamp: ts,
        data: {
          fileName: cc.file_name || cc.fileName,
          language: cc.language,
          action: cc.action || "modify",
          code: cc.code || cc.content,
          original: cc.original,
          diff: cc.diff,
        },
      });
    }

    if (meta.clarification_request || meta.needs_clarification) {
      items.push({
        id: crypto.randomUUID(),
        type: "clarification",
        timestamp: ts,
        data: {
          message: meta.clarification_request?.message || msg.content || "Could you clarify?",
          suggestions: meta.clarification_request?.suggestions || [],
          option_type: meta.clarification_request?.option_type || "file",
          search_enabled: true,
          input_enabled: true,
        },
      });
    } else if (msg.content && !shouldSkipAssistantHistoryMessage(msg.content, meta)) {
      items.push({
        id: crypto.randomUUID(),
        type: "chat_agent",
        timestamp: ts,
        // messageUuid is required by the secure /download-* endpoints — without
        // it the download buttons can't bind the request to a real message.
        data: {
          content: normalizeAssistantContent(msg.content),
          toolCalls: meta.tool_calls || [],
          messageUuid: msg.uuid,
          metadata: meta,
        },
      });
    }

    const savedActions = meta.suggested_actions;
    if (Array.isArray(savedActions) && savedActions.length > 0) {
      useDevAgentStore.getState().setSuggestedActions(savedActions);
    }

    const transparency = meta.transparency;
    if (transparency) {
      const cost = transparency.cost || {};
      const totalCostRaw = cost.total_cost || "0";
      const totalCost =
        typeof totalCostRaw === "string"
          ? parseFloat(totalCostRaw.replace("$", "")) || 0
          : totalCostRaw;
      useDevAgentStore.getState().accumulateTransparencyReport({
        total_cost_usd: totalCost,
        total_duration_ms: transparency.timing?.total_ms || 0,
        tool_calls_count: cost.tool_calls || 0,
        tokens_used: cost.llm_tokens || 0,
        input_tokens: cost.input_tokens || 0,
        output_tokens: cost.output_tokens || 0,
        reasoning_steps: transparency.reasoning || [],
        org_tier: transparency.org_tier,
      });
    }
  }

  if (
    !signal.aborted &&
    useDevAgentStore.getState().sessionUuid === sessionUuid
  ) {
    const existing = useDevAgentStore.getState().feedItems;
    const preserved = existing.filter((f) => {
      if (f.type === "intent_confirm") return true;
      if (f.type === "thinking") return true;
      if (f.type === "approval") return true;
      if (f.type === "impact_analysis") return true;
      if (f.type === "test_coverage") return true;
      if (f.type === "checkpoint") return true;
      if (f.type === "error") return true;
      if (f.type === "tool_call") return true;
      return false;
    });
    // Optimistic `chat_user` rows (e.g. right after "Send to Agent") can exist
    // before the history API returns them — if we only merge `preserved` types,
    // those bubbles (and any markdown table inside them) vanish until the next
    // refresh. Re-attach any local user message whose trimmed body is not yet
    // represented in hydrated history.
    const userBodiesFromHistory = new Set(
      items
        .filter((i) => i.type === "chat_user")
        .map((i) => (i.data.content || "").trim())
        .filter(Boolean),
    );
    const orphanChatUsers = existing
      .filter(
        (f) =>
          f.type === "chat_user" &&
          (f.data.content || "").trim() &&
          !userBodiesFromHistory.has((f.data.content || "").trim()),
      )
      .sort((a, b) => a.timestamp - b.timestamp);
    useDevAgentStore.getState().setFeedItems([
      ...items,
      ...orphanChatUsers,
      ...preserved,
    ]);
  }
}

export async function loadBackendArtifacts(
  sessionUuid: string,
  signal: AbortSignal,
): Promise<void> {
  try {
    const listedArts = await listBackendArtifacts(sessionUuid);
    if (signal.aborted) return;
    if (useDevAgentStore.getState().sessionUuid !== sessionUuid) return;
    const arts = await Promise.all(
      listedArts.map(async (art) => {
        if (art?.content) return art;
        try {
          const full = await getBackendArtifact(art.id);
          if (signal.aborted) return art;
          return full?.missing ? art : { ...art, ...full };
        } catch {
          return art;
        }
      }),
    );
    if (signal.aborted) return;
    if (useDevAgentStore.getState().sessionUuid !== sessionUuid) return;
    // Merge instead of replacing so metadata-only session reloads cannot wipe
    // full artifact content already received from live SSE.
    useDevAgentStore.getState().setArtifacts(arts);
  } catch (err) {
    // Surface the failure so stuck markers don't go unnoticed. Markers will
    // render raw until the next SSE `artifact_complete` populates the store.
    console.warn("[loadBackendArtifacts] failed — markers may render raw:", err);
  }
}

export async function loadDevAgentPinnedArtifacts(
  sessionUuid: string,
  signal: AbortSignal,
): Promise<void> {
  try {
    const artifacts = await listArtifacts(sessionUuid);
    if (signal.aborted) return;
    if (useDevAgentStore.getState().sessionUuid !== sessionUuid) return;
    if (!artifacts.length) return;
    const store = useDevAgentStore.getState();
    const pinned: Record<string, boolean> = {};
    for (const a of artifacts) {
      pinned[a.id] = true;
      if (
        !store.feedItems.find(
          (f) => f.type === "tool_call" && f.data.metadataId === a.id,
        )
      ) {
        store.addFeedItem({
          type: "tool_call",
          data: {
            name: "read_source_code",
            args: { name: a.name },
            status: "done",
            output: "loaded",
            metadataType: a.metadataType || "ApexClass",
            metadataId: a.id,
          },
        });
      }
    }
    useDevAgentStore.setState({ pinnedArtifactIds: pinned });
  } catch {
    /* non-critical */
  }
}
